/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useSetting } from '@airasia-rn/account';
import { useMemo } from 'react';

interface SettingResponse {
  value: string | number | null | undefined;
}

export const useParsedSettings = <K extends string>(
  settingKeys: K[],
): Record<K, number | string | null> => {
  const settings = settingKeys.map((key) =>
    useSetting(key),
  ) as SettingResponse[];

  return useMemo(() => {
    return settings.reduce(
      (acc, { value }, index) => {
        const key = settingKeys[index];

        // Parsing logic
        const parsedValue =
          value !== null && value !== '' && !isNaN(Number(value))
            ? Number(value)
            : (value ?? null);

        acc[key] = parsedValue;
        return acc;
      },
      {} as Record<K, number | string | null>,
    );

    // We use JSON.stringify on the values to keep the dependency stable
  }, [JSON.stringify(settings.map((s) => s.value)), settingKeys]);
};
